import { BuyerEntry } from './dataloaders/types';

/**
 * Extended BuyerEntry with calculated tickets in lottery pool
 */
export interface BuyerEntryWithLottery extends BuyerEntry {
    /** Total tickets in the lottery pool (purchased + bonus) */
    ticketsInLottery: number;
    /** Consecutive ticket number range (e.g., "1-12" or "13") */
    ticketNumberRange: string;
    /** Start ticket number for this buyer */
    ticketNumberStart: number;
    /** End ticket number for this buyer */
    ticketNumberEnd: number;
}

/**
 * Calculates the number of bonus tickets based on purchased tickets.
 * 
 * Rules:
 * - Buy 10 tickets → get 2 bonus (12 total for 50 Euro)
 * - Buy 20 tickets → get 5 bonus (25 total for 100 Euro)
 * 
 * The bonus is applied for each complete batch:
 * - For every 10 tickets: 2 bonus tickets (applies for tickets 10-19)
 * - For every 20 tickets: 5 bonus tickets (applies for the full 20)
 * 
 * @param ticketsPurchased Number of tickets purchased
 * @returns Number of bonus tickets
 */
export function calculateBonusTickets(ticketsPurchased: number): number {
    if (ticketsPurchased < 10) {
        return 0;
    }
    
    // Calculate how many complete sets of 20 tickets
    const setsOf20 = Math.floor(ticketsPurchased / 20);
    // Calculate remaining tickets after sets of 20
    const remaining = ticketsPurchased % 20;
    
    // Each set of 20 gives 5 bonus tickets
    let bonus = setsOf20 * 5;
    
    // If remaining is >= 10, add 2 bonus tickets for that batch
    if (remaining >= 10) {
        bonus += 2;
    }
    
    return bonus;
}

/**
 * Calculates total tickets in the lottery pool (purchased + bonus)
 * 
 * @param ticketsPurchased Number of tickets purchased
 * @returns Total tickets in lottery pool
 */
export function calculateTicketsInLottery(ticketsPurchased: number): number {
    return ticketsPurchased + calculateBonusTickets(ticketsPurchased);
}

/**
 * Combines multiple buyer entries with the same email address.
 * The combination sums up the tickets purchased.
 * Names are taken from the first occurrence.
 * IDs are concatenated with commas.
 * 
 * @param entries Array of buyer entries
 * @returns Array of combined buyer entries (one per unique email)
 */
export function combineBuyersByEmail(entries: BuyerEntry[]): BuyerEntry[] {
    const emailMap = new Map<string, BuyerEntry>();
    
    let noEmailCounter = 0;
    for (const entry of entries) {
        const normalizedEmail = entry.email.toLowerCase().trim();
        
        if (!normalizedEmail) {
            // If no email, keep as separate entry with unique key using a counter
            emailMap.set(`__no_email_${entry.id || noEmailCounter++}`, entry);
            continue;
        }
        
        const existing = emailMap.get(normalizedEmail);
        if (existing) {
            // Combine with existing entry
            emailMap.set(normalizedEmail, {
                ...existing,
                id: existing.id ? `${existing.id}, ${entry.id}` : entry.id,
                ticketsPurchased: existing.ticketsPurchased + entry.ticketsPurchased,
            });
        } else {
            emailMap.set(normalizedEmail, { ...entry });
        }
    }
    
    return Array.from(emailMap.values());
}

/**
 * Formats a ticket number range string.
 * Returns the start number if only one ticket, otherwise returns "start-end".
 * 
 * @param startNumber First ticket number in the range
 * @param count Number of tickets in the range
 * @returns Formatted range string (e.g., "1-12" or "13")
 */
export function formatTicketNumberRange(startNumber: number, count: number): string {
    if (count === 1) {
        return String(startNumber);
    }
    const endNumber = startNumber + count - 1;
    return `${startNumber}-${endNumber}`;
}

/**
 * Processes buyer entries: combines by email and adds lottery ticket calculation
 * and consecutive ticket number ranges.
 * 
 * @param entries Array of buyer entries
 * @returns Array of processed entries with lottery tickets calculated
 */
export function processBuyerEntries(entries: BuyerEntry[]): BuyerEntryWithLottery[] {
    const combined = combineBuyersByEmail(entries);
    
    let currentTicketNumber = 1;
    
    return combined.map(entry => {
        const ticketsInLottery = calculateTicketsInLottery(entry.ticketsPurchased);
        const ticketNumberRange = formatTicketNumberRange(currentTicketNumber, ticketsInLottery);
        const ticketNumberStart = currentTicketNumber;
        const ticketNumberEnd = currentTicketNumber + ticketsInLottery - 1;
        currentTicketNumber += ticketsInLottery;
        
        return {
            ...entry,
            ticketsInLottery,
            ticketNumberRange,
            ticketNumberStart,
            ticketNumberEnd,
        };
    });
}

/**
 * Filters buyer entries based on search query.
 * Searches in email and name fields (case-insensitive).
 * 
 * @param entries Array of buyer entries
 * @param query Search query string
 * @returns Filtered array of buyer entries
 */
export function filterBuyerEntries<T extends BuyerEntry>(entries: T[], query: string): T[] {
    if (!query.trim()) {
        return entries;
    }
    
    const normalizedQuery = query.toLowerCase().trim();
    
    return entries.filter(entry => {
        const emailMatch = entry.email.toLowerCase().includes(normalizedQuery);
        const nameMatch = entry.name.toLowerCase().includes(normalizedQuery);
        return emailMatch || nameMatch;
    });
}

/**
 * Creates a seeded pseudo-random number generator using the SplitMix32 algorithm.
 * This is a deterministic PRNG that produces reproducible sequences given the same seed.
 * 
 * @param seedHex - A hexadecimal string (e.g., SHA-256 hash) to use as the seed
 * @returns A function that generates the next pseudo-random number in range [0, 1)
 */
export function createSeededRandom(seedHex: string): () => number {
    // Helper to safely parse hex string, returning fallback if invalid
    const safeParseHex = (hex: string): number => {
        const parsed = parseInt(hex, 16);
        // Check for NaN (invalid hex characters)
        if (Number.isNaN(parsed)) {
            return 0;
        }
        return parsed >>> 0;
    };
    
    // Convert the first 8 hex characters to a 32-bit integer seed
    // This gives us a deterministic starting point based on the SHA-256 hash
    let state = safeParseHex(seedHex.substring(0, 8));
    
    // Use additional parts of the hash to further mix the initial state
    if (seedHex.length >= 16) {
        state ^= safeParseHex(seedHex.substring(8, 16));
    }
    
    // Ensure state is never 0 (which would break the generator)
    if (state === 0) {
        state = 0x9E3779B9; // Golden ratio constant as fallback
    }
    
    // SplitMix32 algorithm - a fast, high-quality PRNG
    return function random(): number {
        state = (state + 0x9E3779B9) >>> 0;
        let z = state;
        z = Math.imul(z ^ (z >>> 16), 0x85EBCA6B) >>> 0;
        z = Math.imul(z ^ (z >>> 13), 0xC2B2AE35) >>> 0;
        z = (z ^ (z >>> 16)) >>> 0;
        // Convert to [0, 1) range
        return z / 0x100000000;
    };
}

/**
 * Generates a random integer in range [min, max] (inclusive) using a seeded random generator.
 * 
 * @param random - A seeded random function that returns values in [0, 1)
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (inclusive)
 * @returns A random integer in the specified range
 */
export function getRandomInt(random: () => number, min: number, max: number): number {
    return Math.floor(random() * (max - min + 1)) + min;
}

/**
 * Finds the buyer who owns a specific ticket number.
 * 
 * @param entries - Array of processed buyer entries with ticket ranges
 * @param ticketNumber - The ticket number to look up
 * @returns The buyer entry that owns the ticket, or null if not found
 */
export function findBuyerByTicketNumber(
    entries: BuyerEntryWithLottery[],
    ticketNumber: number
): BuyerEntryWithLottery | null {
    for (const entry of entries) {
        if (ticketNumber >= entry.ticketNumberStart && ticketNumber <= entry.ticketNumberEnd) {
            return entry;
        }
    }
    return null;
}

/**
 * Calculates the total number of tickets in the lottery pool.
 * 
 * @param entries - Array of processed buyer entries
 * @returns Total number of tickets
 */
export function getTotalTickets(entries: BuyerEntryWithLottery[]): number {
    if (entries.length === 0) {
        return 0;
    }
    // The last entry's ticketNumberEnd is the total count
    return entries[entries.length - 1].ticketNumberEnd;
}
